import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var configApiEndpoints = {
    /* For each endpoint specify
     - a pathname (starting with slash) and relative to that api's baseUrl (without url params or '?')
     - request method ('POST', 'GET', 'PUT' etc.)
     - rest flag indicates 'restful' urls
        - if true, apiHelper will scan path for any variables like '/user/{userId}'
          and if such variable is present in requestData, update path with its value,
          removing the variable from requestData itself.
        - if false path remains static and requestData remains unchanged
     - skipAuth flag - tells apiHelper to skip adding authentication to call
    */
    auth: {
        authenticate: {
            path: '/authentication/authenticate',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        validate: {
            path: '/authentication/validateaccesstoken',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        logout: {
            path: '/authentication/logout',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        createPassword: {
            path: '/authentication/createpassword',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        studentLogin: {
            path: '/authenticationelevatestudent/',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        //TODO: rationalize teacherLogin and base authenticate above
        teacherLogin: {
            path: '/authentication/authenticate',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        sendResetPassEmail: {
            path: '/authentication/passwordverification',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        resendPassword: {
            path: '/resendpassword',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        resetPassword: {
            path: '/authentication/resetpassword',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        hasSetInitialPassword: {
            path: '/authentication/hassetinitialpassword/{userId}',
            method: 'GET',
            rest: true,
        },
        refreshToken: {
            path: '/authentication/refreshtoken',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        SsoLogin: {
            path: '/authentication/ssologin',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
    },
    rostering: {
        stubHttp: {
            path: '/stubhttp',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        startCleverSync: {
            path: '/roster/clever/sync/start',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        cleverSyncHistory: {
            path: '/roster/syncdetails',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        cancelCleverSync: {
            path: '/roster/cancelcleversync',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        clever: {
            path: '/roster/syncdetails',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        rosteringRoles: {
            path: '/roster/rosteringroles',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        studentProctor: {
            path: '/roster/studentproctor',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        proctorcodereset: {
            path: '/roster/proctorcodereset',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        proctorcodevalidation: {
            path: '/roster/student/proctorcode/validation',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        studentDemographic: {
            path: '/roster/studentdemographic',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        usersearch: {
            path: '/roster/usersearch',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        userschools: {
            path: '/roster/userschools',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        sectiondetails: {
            path: '/roster/sectiondetails',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        sync: {
            path: '/sync/trigger',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        testtakertickets: {
            path: '/roster/testtakertickets',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        testtakerticketscount: {
            path: '/roster/testtakerticketscount',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        grades: {
            path: '/roster/grades',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        studentcountbygrades: {
            path: '/roster/studentcountbygrades',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        locationsectiondetails: {
            path: '/roster/locationsectiondetails',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        getGradesOfUser: {
            path: '/roster/grades',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        getCodeForCleverSyncLink: {
            path: '/roster/liwcsecret',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        saveUserPermissions: {
            path: '/roster/customuserpermission',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        ssrSyncDetails: {
            path: '/roster/ssrsyncdetails',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        ssrsyncerrors: {
            path: '/roster/ssrsyncerrors',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
    },
    rosteringImport: {
        validateFile: {
            path: '/validatefile',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        upload: {
            path: '/upload',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        abort: {
            path: '/abort',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        downloadFile: {
            path: '/downloadfile',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        uploadHistory: {
            path: '/uploadHistory',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        viewHierarchy: {
            path: '/viewhierarchy',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        downloadErrorsAndWarnings: {
            path: '/downloaderrorandwarnings-v2',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        initialize: {
            path: '/initialize',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        uploadmultipart: {
            path: '/uploadmultipart',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        finalize: {
            path: '/finalize',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        downloadKioskBrowserOEM: {
            path: '/download/respondus/lockdown/{browseroem}',
            method: 'GET',
            rest: true,
            skipAuth: false,
        },
        studentDownloadBrowserOEM: {
            path: '/student/download/lockdown/{browseroem}',
            method: 'GET',
            rest: true,
            skipAuth: false,
        },
    },
    profile: {
        userInfo: {
            path: '/user/{userId}',
            method: 'GET',
            rest: true,
            skipAuth: false,
        },
        SaveAvatarSelection: {
            path: '/user/avatar',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        SaveTermsOfUseAndPrivacyPolicy: {
            path: '/user/termsofuse',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
    },
    license: {
        orderHistory: {
            path: '/license/orderhistory/{customerId}/{offset}/{limit}/{sortProp}/{sortDirection}',
            method: 'GET',
            rest: true,
            skipAuth: false,
        },
        getLicenseConsumption: {
            path: '/license/licenseconsumption/{customerId}',
            method: 'GET',
            rest: true,
            skipAuth: false,
        },
        getBucketLicenseConsumption: {
            path: '/license/bucketlicenseconsumption/{customerId}/{productId}/{startDate}/{endDate}',
            method: 'GET',
            rest: true,
            skipAuth: false,
        },
    },
    fileservices: {
        uploadRostering: {
            path: '/upload/rostering',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        errorlist: {
            path: '/download/errorlist',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        downloadKioskBrowserOEM: {
            path: '/download/respondus/{browseroem}',
            method: 'GET',
            rest: true,
            skipAuth: false,
        },
    },
    settings: {
        getAllRolePermissions: {
            path: '/rolepermissions/byroletype',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        getAllCustomerRoles: {
            path: '/roles',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
    },
    proctoringApiUrl: {
        getKioskBrowserAllowlist: {
            path: '/proctor/external/respondus/allowlist/{customerId}',
            method: 'GET',
            rest: true,
        },
        postKioskBrowserAllowlist: {
            path: '/proctor/external/respondus/allowlist',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        bte: {
            path: '/proctor/bte',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        reOpenStudentSessionExternal: {
            path: '/proctor/external/session/reopen',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        onStudentStatusUpdateExternal: {
            path: '/proctor/status/update/reopenexternal',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        onStudentStatusUpdate: {
            path: '/student/status/update',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        OnStudentStatusChange: {
            path: '/api/Proctoring/OnStudentStatusChange',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        OnStudentExtendTime: {
            path: '/proctor/extendtime',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        ProctorAblyToken: {
            path: '/proctor/ably/token/{clientid}',
            method: 'GET',
            rest: true,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
        StudentAblyToken: {
            path: '/student/ably/token/{clientid}',
            method: 'GET',
            rest: true,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
        ProctorSession: {
            path: '/proctor/session',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        InitLernosityReportApi: {
            //path: '/proctoring/proctor/report/{proctorsessionid}',
            path: '/proctor/report',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        GetWaitingApprovalStudents: {
            path: '/proctor/waitingapproval',
            method: 'GET',
            rest: false,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
        GetInProgressStudents: {
            path: '/proctor/inprogresss',
            method: 'GET',
            rest: false,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
        GetCompletedStudents: {
            path: '/proctor/testcomplete',
            method: 'GET',
            rest: false,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
        OnBulkStudentsStatusChange: {
            path: '/proctor/status/bulk/change',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        OnStudentSideBulkStudentsStatusChange: {
            path: '/student/status/bulk/change',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        LearnosityAddUsers: {
            path: '/proctor/report/add',
            method: 'POST',
            rest: false,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
        ElevateDataApiSessions: {
            path: '/proctor/data/sessions',
            method: 'GET',
            rest: false,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
        OnStudentCancel: {
            path: '/proctor/cancel',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        DecodeKioskBrowserStudentCode: {
            path: '/student/respondus/student/decrypted/{encryptedcode}',
            method: 'GET',
            rest: true,
            skipAuth: true,
        },
        GetKioskBrowserPopupContent: {
            path: '/student/respondus/popup',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        LDBGenerateHandshakeChallenge: {
            path: '/student/respondus/challenge',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        LDBValidateHandshakeResponse: {
            path: '/student/respondus/response',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        GetStudentLandingAssignments: {
            path: '/student/landing/assignments',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        GetStudentLandingDisabledAssignments: {
            path: '/student/landing/disabled/assignments',
            method: 'GET',
            rest: true,
            skipAuth: false,
        },
        getExternalStudentLandingAssignments: {
            path: '/proctor/external/landing/assignments',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        OnStudentSelectLandingAssignment: {
            path: '/student/landing/assignment/select',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        GetLearnosityStudentInit: {
            path: '/student/items/sections/init',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        LearnosityGlobalLogging: {
            path: '/student/error/log',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        UpdateStudentSessionExtendTime: {
            path: '/student/update/session/extendtime',
            method: 'POST',
            rest: false,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
        UpdateStudentSessionMetaData: {
            path: '/student/update/session/metadata',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        AssignmentActiveTesting: {
            path: '/proctor/assignment/active/testing/{assignmentId}',
            method: 'GET',
            rest: true,
        },
        StudentCount: {
            path: '/proctor/studentcount',
            method: 'POST',
            rest: true,
        },
    },
    administrationApiUrl: {
        getPermissionMap: {
            path: '/permissions',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        getAllRolePermissions: {
            path: '/rolepermissions/byroletype',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        getAllCustomerRoles: {
            path: '/roles',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        getAllCustomerRolesStudentAuth: {
            path: '/roles/student',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
        updateRolePermissions: {
            path: '/rolepermissions',
            method: 'PUT',
        },
        lockpermission: {
            path: '/customers/lockpermission',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        getCustomUserPermissions: {
            path: '/customuserpermissions',
            method: 'GET',
            rest: false,
            skipAuth: false,
        },
    },
    reportingApiUrl: {
        getStudentCogatHistory: {
            path: '/studentcogathistory/{studentId}',
            method: 'GET',
            rest: true,
        },
        testTakerFilters: {
            path: '/testtakerreportfilters',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        testTakerReport: {
            path: '/studentassignmentstatusreport/studentassignmentstatuscount',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        DownloadCSV: {
            path: '/exportdata/downloadtesttakerreport',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        TestAssignments: {
            path: '/filters/newtestassignments',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        Grades: {
            path: '/filters/grades',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        Locations: {
            path: '/filters/locations',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        Buildings: {
            path: '/filters/buildings',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        GetAgeStanine: {
            path: '/agestanine',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        GetStudentScores: {
            path: '/testtakerstatusstudentscore',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        ReportingExtendTime: {
            path: '/extendtime',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
    },
    assignmentApiUrl: {
        RptAssignmentsByCustomer: {
            path: '/customer/{customerId}/rpt',
            method: 'GET',
            rest: true,
        },
        GetAssigmentsByCustomerAndGrade: {
            path: '/customer/assignments',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        SchoolYearByCustomer: {
            path: '/customer/{customerid}/schoolyear',
            method: 'GET',
            rest: true,
        },
        ViewAssignmentsByGrade: {
            path: '/assignment/viewbygrade',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        ViewTrashedAssignmentsByGrade: {
            path: '/assignment/view/trashedbygrade',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        EntitlementsByCustomer: {
            path: '/customer/{customerid}/entitlements',
            method: 'GET',
            rest: true,
        },
        DuplicateAssignment: {
            path: '/assignment/duplicate',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        SaveAssignment: {
            path: '/assignment/save',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        GetAssignmentById: {
            path: '/assignment/{assignmentid}',
            method: 'GET',
            rest: true,
        },
        DeleteAssignmentByAssignmentId: {
            path: '/assignment/{assignmentId}',
            method: 'DELETE',
            rest: true,
        },
        RestoreAssignments: {
            path: '/restore',
            method: 'POST',
            rest: false,
        },
        CloseAssignmentByAssignmentId: {
            path: '/assignment/close',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        ReopenAssignmentByAssignmentId: {
            path: '/assignment/reopen',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        UpdateAssignmentStatus: {
            path: '/assignment/update/assignmentstatus',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
        UpdateAssignmentEditableStatus: {
            path: '/assignment/student/editable',
            method: 'PUT',
            rest: false,
            skipAuth: false,
            delay: 1000,
            attempts: 3,
            counter: 0,
        },
    },
    assignment: {
        GetAssignmentById: {
            path: '/assignmentid',
            method: 'POST',
            rest: false,
            skipAuth: false,
        },
    },
    kioskBrowserApiUrl: {
        storeSessionToken: {
            path: '/store',
            method: 'POST',
            rest: false,
            skipAuth: true,
        },
        restoreSessionToken: {
            path: '/restore',
            method: 'POST',
            rest: false,
            skipAuth: true,
        },
    },
    integrationsApi: {
        ablyTokenRequest: {
            path: '/ably/request',
            method: 'POST',
            rest: true,
            skipAuth: false,
        },
    },
};

var generateApiConfig = function (apiDomain) { return ({
    baseUrl: {
        auth: "https://".concat(apiDomain, "/identity"),
        rostering: "https://".concat(apiDomain, "/rostering"),
        rosteringImport: "https://".concat(apiDomain, "/fileupload/file"),
        profile: "https://".concat(apiDomain, "/userprofile"),
        license: "https://".concat(apiDomain, "/licensing"),
        fileservices: "https://".concat(apiDomain, "/fileservices"),
        administrationApiUrl: "https://".concat(apiDomain, "/admin"),
        assignmentApiUrl: "https://".concat(apiDomain, "/assignments"),
        reportingApiUrl: "https://".concat(apiDomain, "/reporting"),
        proctoringApiUrl: "https://".concat(apiDomain, "/proctoring"),
        kioskBrowserApiUrl: "https://".concat(apiDomain, "/integrations/respondus"),
        integrationsApi: "https://".concat(apiDomain, "/integrations"),
        learnosityItemApiUrl: 'https://items.learnosity.com/?v2023.1.LTS',
        learnosityReportApiUrl: 'https://reports.learnosity.com/?v2023.1.LTS',
        // "ca" suffix is for load testing on Canadian servers rather than our main us-east-1 servers.
        learnosityItemApiUrlLoadTestingModeOn: 'https://items-ca.learnosity.com/?v2023.1.LTS',
        learnosityReportApiUrlLoadTestingModeOn: 'https://reports-ca.learnosity.com/?v2023.1.LTS',
    },
    endpoints: configApiEndpoints,
    defaultRequestSetup: {
        headers: {
        // 'Access-Control-Allow-Origin': '*',
        },
        withCredentials: false,
    },
    cancelExceptionMessage: 'Aborted by user',
}); };

var generateAwsConfig = function (_a) {
    var region = _a.region, identityPoolId = _a.identityPoolId, userPoolId = _a.userPoolId, cognitoClientId = _a.cognitoClientId, customField = _a.customField;
    var awsConfig = {
        region: region,
        IdentityPoolId: identityPoolId,
        UserPoolId: userPoolId,
        ClientId: cognitoClientId,
    };
    var studentAwsConfig = {
        region: region,
        IdentityPoolId: identityPoolId,
        UserPoolId: userPoolId,
        ClientId: cognitoClientId,
        customField: customField,
    };
    return { awsConfig: awsConfig, studentAwsConfig: studentAwsConfig };
};

// the configurations in this file are unlikely to change, so no ability to override is provided
/**
 * Names for cookies used by the application.
 * TODO: this doesn't need to be configuration and should be constants in the code
 */
var cookieNames = {
    staffIdentity: 'elevateStaff',
    studentIdentity: 'elevateStudent',
    enableDebug: 'elevateDebugEnabled',
    currentStaffRole: 'currentStaffRole',
};
var rosteringImportOptions = {
    multipartUploadChunkSize: 5 * 1024 * 1024,
    maximumFileSize: 100 * 1024 * 1024, // 100 MB
};
var studentPortfolioOptions = {
    minTimedAssignmentLevel: 9,
    maxExtendAssignmentMinutes: 99,
};
var pathConfig = {
    // UI will SKIP authenticating users on pages whose URLS start with values of members of the array
    authPaths: ['auth'],
    skipAuthPaths: ['respondus', 'login', 'logout', 'create-password', 'sso'],
    // UI will SHOW ERROR pages for pages whose pathnames DON'T BEGIN with one of array member values
    validRootPathnames: ['auth', 'user', 'rostering', 'clever', 'student', 'check-session'],
    // UI will HIDE the header from pages for pages whose pathnames begin with one of array member values
    noHeaderRootPathnames: ['auth'],
};
var baseConfig = __assign({ local: false, cookieNames: cookieNames, rosteringImportOptions: rosteringImportOptions, studentPortfolioOptions: studentPortfolioOptions }, pathConfig);

var generateCleverSsoConfig = function (_a) {
    var cognitoUrl = _a.cognitoUrl, userPoolId = _a.userPoolId, cognitoClientId = _a.cognitoClientId, cleverClientId = _a.cleverClientId, elevateUrl = _a.elevateUrl;
    var redirectUri = encodeURIComponent("".concat(elevateUrl, "/administration/auth/sso"));
    return {
        cleverSSO: {
            cognito: {
                authentication: {
                    url: "".concat(cognitoUrl, "/oauth2/token"),
                    params: {
                        grantType: 'authorization_code',
                        redirectUrl: "".concat(elevateUrl, "/administration/auth/sso"),
                    },
                },
                clientId: cognitoClientId,
                userPoolId: userPoolId,
                logout: {
                    staff: {
                        url: "".concat(cognitoUrl, "/logout"),
                        params: {
                            redirect_uri: "".concat(elevateUrl, "/administration/auth/sso"),
                            logout_uri: "".concat(elevateUrl, "/administration/auth/logout"),
                            client_id: cognitoClientId,
                            response_type: 'code',
                            state: 'STATE',
                            scope: 'openid',
                        },
                    },
                    student: {
                        url: "".concat(cognitoUrl, "/logout"),
                        params: {
                            redirect_uri: "".concat(elevateUrl, "/administration/auth/sso"),
                            logout_uri: "".concat(elevateUrl, "/administration/auth/logout/student"),
                            client_id: cognitoClientId,
                            response_type: 'code',
                            state: 'STATE',
                            scope: 'openid',
                        },
                    },
                },
            },
            liwc: {
                url: 'https://clever.com/oauth/authorize',
                params: {
                    responseType: 'code',
                    redirectURI: "".concat(cognitoUrl, "/oauth2/authorize?client_id=").concat(cognitoClientId, "&response_type=code&scope=email+openid+profile&redirect_uri=").concat(redirectUri, "&identity_provider=Clever"),
                    clientId: cleverClientId,
                },
            },
        },
    };
};
var generateCleverSyncConfig = function (client_id, apiDomain) { return ({
    cleverSync: {
        startSyncUrl: {
            baseUrl: 'https://clever.com',
            pathname: '/oauth/authorize',
            urlParams: {
                client_id: client_id,
                response_type: 'code',
                redirect_uri: "https://".concat(apiDomain, "/rostering/roster/liwcvalidation"),
            },
        },
    },
}); };

var generateLoggingOptions = function (overrides) { return (__assign({ 
    // General logging flag
    enabled: true, 
    // Flags for log level output
    logLevels: {
        debug: false,
        info: false,
        warning: false,
        error: false,
    }, 
    // Log to browser console (default)
    logToConsole: true, 
    // Log additional data for each log entry to browser console (only UI)
    logAdditionalDataToConsole: false, 
    // Send log messages to persistent log (currently not implemented, will use cloudWatch)
    logToPersistentLog: false, 
    // style logging messages in the console
    styleConsoleMessages: true, namespaceStyles: [
        'background: #F8F8F8; border: 1px solid #444444; color: #000000; padding: 3px 6px; font-weight: bold;',
        'border: none; color: unset; padding: unset; font-weight: normal',
    ], logStyles: {
        log: [
            'background: #DDDDDD; color: #333333; padding: 3px 6px;',
            'background: unset; color: unset; padding: unset;',
        ],
        debug: [
            'background: #DDDDDD; color: #333333; padding: 3px 6px;',
            'background: unset; color: unset; padding: unset;',
        ],
        info: [
            'background: #229922; color: #FFFFFF; padding: 3px 6px;',
            'background: unset; color: unset; padding: unset;',
        ],
        warning: [
            'background: #FFCC33; color: #333333; padding: 3px 6px;',
            'background: unset; color: unset; padding: unset;',
        ],
        warn: [
            'background: #FFCC33; color: #333333; padding: 3px 6px;',
            'background: unset; color: unset; padding: unset;',
        ],
        error: [
            'background: #CC3333; color: #FFFFFF; padding: 3px 6px;',
            'background: unset; color: unset; padding: unset;',
        ],
    }, debugFilters: {
        namespaces: [],
        exclude: false,
    } }, overrides)); };

// Configuration related to proctoring / student test taking + Ably
var generateProctoringConfig = function (overrides) { return ({
    proctoring: __assign({ ably: {
            heartbeatInterval: 5000,
            remainPresentFor: 30000,
            customActions: true,
            customActionsTimerInitialSeconds: 10,
            connectionUserPrefix: 'user-',
        }, studentSideAutoApproveCustomerIds: [''], flgAutoApprove: false, autoApproveSettings: {
            minSecondsDelay: 10,
            maxSecondsDelay: 30,
        }, arrPostScreenerSubTestAcronyms: ['VC', 'PC', 'SC', 'NS', 'NP', 'FC', 'PF'], tutorial: {
            cogatLowerLevel: 'Lower Level Getting Started|2|0',
            cogatUpperLevel: 'Upper Level Getting Started|3|0',
        }, maxStudentsForLearnosityReportApi: 100, isUpdateStudentSessionMetaDataLoggerEnabled: false }, overrides),
}); };

var generateSessionConfig = function (overrides) { return ({
    session: __assign({ cacheUser: true, cacheType: 'localStorage', ssoLogoutCookieName: 'tempSSO', ssoPreLogoutCodeStorageName: 'cleverSSOCode', lastActivityCookie: 'lastUserActivity', lastBlurCookie: 'lastBlurTimestamp', forgotPasswordCodeValidityPeriodHours: 1, staff: {
            idTokenCookie: 'idToken',
            accessTokenCookie: 'accessToken',
            refreshTokenCookie: 'refreshToken',
            userCacheKey: 'elevateUser',
            cacheUser: true,
            cacheType: 'localStorage',
            storeInCookies: true,
            inactivityLogoutMinutes: 15.0,
            inactivityModalMinutes: 2.0,
        }, student: {
            idTokenCookie: 'sidToken',
            accessTokenCookie: 'saccessToken',
            refreshTokenCookie: 'srefreshToken',
            proctorCodeCookie: 'studentProctorCode',
            userCacheKey: 'elevateStudentUser',
            cacheUser: true,
            cacheType: 'localStorage',
            storeInCookies: false,
            inactivityLogoutMinutes: 38.0,
            inactivityModalMinutes: 2.0,
        } }, overrides),
}); };

function generateConfig(loadedConfig) {
    var apiDomain = loadedConfig.apiDomain, appEnvironment = loadedConfig.appEnvironment, cleverClientId = loadedConfig.cleverClientId, cognitoClientId = loadedConfig.cognitoClientId, cognitoUrl = loadedConfig.cognitoUrl, customField = loadedConfig.customField, elevateUrl = loadedConfig.elevateUrl, identityPoolId = loadedConfig.identityPoolId, local = loadedConfig.local, logging = loadedConfig.logging, logRocketAppId = loadedConfig.logRocketAppId, region = loadedConfig.region, rootCookieDomain = loadedConfig.rootCookieDomain, splitApiKey = loadedConfig.splitApiKey, userPoolId = loadedConfig.userPoolId, webReportingDomain = loadedConfig.webReportingDomain;
    var api = generateApiConfig(apiDomain);
    var awsConfigObj = generateAwsConfig({
        region: region,
        identityPoolId: identityPoolId,
        userPoolId: userPoolId,
        cognitoClientId: cognitoClientId,
        customField: customField,
    });
    var cleverSyncObj = generateCleverSyncConfig(cleverClientId, apiDomain);
    var cleverSSOObj = generateCleverSsoConfig({
        cognitoUrl: cognitoUrl,
        elevateUrl: elevateUrl,
        cognitoClientId: cognitoClientId,
        cleverClientId: cleverClientId,
        userPoolId: userPoolId,
    });
    var loggingOptions = generateLoggingOptions(logging);
    var proctoring = generateProctoringConfig();
    var sessionObj = generateSessionConfig();
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign({}, awsConfigObj), baseConfig), cleverSyncObj), cleverSSOObj), sessionObj), proctoring), { api: api, appEnvironment: appEnvironment, local: local, loggingOptions: loggingOptions, logRocketAppId: logRocketAppId, rootCookieDomain: rootCookieDomain, splitApiKey: splitApiKey, webReportingDomain: webReportingDomain });
}

/**
 * Fetches configuration data asynchronously from a JSON file, appending a version query parameter.
 *
 * @throws {Error} Throws an error if the request fails or the response is not OK.
 * @returns {Promise<CoreConfig>} A promise that resolves to the configuration data.
 */
var fetchConfig = function () { return __awaiter(void 0, void 0, void 0, function () {
    var version, response;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                version = Date.now();
                return [4 /*yield*/, fetch("".concat(process.env.PUBLIC_URL, "/config.json?version=").concat(version))];
            case 1:
                response = _a.sent();
                if (!response.ok) {
                    throw new Error('Failed to load configuration');
                }
                return [2 /*return*/, response.json()];
        }
    });
}); };
/**
 * Custom hook for loading configuration data.
 *
 * @returns {LoadConfigResults} The result of the query.
 */
var useLoadConfig = function () {
    var _a = useQuery('config', fetchConfig, {
        retry: 1,
    }), coreConfig = _a.data, isLoading = _a.isLoading, isError = _a.isError, error = _a.error;
    var _b = useState(null), config = _b[0], setConfig = _b[1];
    useEffect(function () {
        if (!isLoading && coreConfig) {
            setConfig(generateConfig(coreConfig));
        }
    }, [isLoading, coreConfig]);
    return { config: config, isLoading: isLoading, isError: isError, error: error };
};

export { fetchConfig, useLoadConfig };
